import * as React from 'react'
import { cn } from '@/lib/utils'

type RadioGroupContextValue = {
  name: string
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(
  null
)

export interface RadioGroupProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'defaultValue'
> {
  name: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  disabled?: boolean
  orientation?: 'horizontal' | 'vertical'
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      name,
      value,
      defaultValue,
      onChange,
      disabled,
      orientation = 'vertical',
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const [internal, setInternal] = React.useState<string | undefined>(
      defaultValue
    )
    const current = value ?? internal
    const handle = (v: string) => {
      if (value === undefined) setInternal(v)
      onChange?.(v)
    }
    return (
      <div
        ref={ref}
        role="radiogroup"
        className={cn(
          'flex',
          orientation === 'vertical' ? 'flex-col gap-2' : 'flex-row gap-4',
          className
        )}
        {...rest}
      >
        <RadioGroupContext.Provider
          value={{ name, value: current, onChange: handle, disabled }}
        >
          {children}
        </RadioGroupContext.Provider>
      </div>
    )
  }
)
RadioGroup.displayName = 'RadioGroup'

const sizeMap = {
  sm: { box: 'h-4 w-4', dot: 'h-1.5 w-1.5', text: 'text-xs' },
  md: { box: 'h-5 w-5', dot: 'h-2 w-2', text: 'text-sm' },
  lg: { box: 'h-6 w-6', dot: 'h-2.5 w-2.5', text: 'text-base' },
} as const

export interface RadioProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  label?: React.ReactNode
  size?: keyof typeof sizeMap
  value: string
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, size = 'md', disabled, value, ...rest }, ref) => {
    const group = React.useContext(RadioGroupContext)
    const sizing = sizeMap[size]
    const autoId = React.useId()
    const id = rest.id ?? autoId

    const name = group?.name ?? rest.name
    const checked = group?.value === value
    const isDisabled = disabled ?? group?.disabled

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) group?.onChange?.(value)
      rest.onChange?.(e)
    }

    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex items-center gap-2',
          isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          className
        )}
      >
        <span className="relative inline-flex">
          <input
            ref={ref}
            type="radio"
            id={id}
            name={name}
            value={value}
            checked={checked}
            disabled={isDisabled}
            onChange={onChange}
            className={cn(
              'peer appearance-none rounded-full border border-gray-300 bg-white transition-colors checked:border-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800',
              sizing.box
            )}
            {...rest}
          />
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500 opacity-0 transition-opacity peer-checked:opacity-100',
              sizing.dot
            )}
          />
        </span>
        {label && (
          <span
            className={cn(
              'select-none text-gray-800 dark:text-gray-200',
              sizing.text
            )}
          >
            {label}
          </span>
        )}
      </label>
    )
  }
)
Radio.displayName = 'Radio'
